/**
 * focusModes — the single source of truth for the Focus page's three modes.
 *
 * Four components used to encode "which paths are Focus" independently: the
 * Focus page itself, Header, MobileHeader and MobileBottomNav. They disagreed —
 * MobileHeader tested `/focus || /flows`, and `/flows` only ever redirects to
 * `/circuits`, so its mode toggle was dead on the one route that needed it.
 * Everything reads this module now, so the set cannot drift again.
 *
 * DATA ONLY — no icon components live here. FocusModeTabs owns the icon map, so
 * a consumer that only needs path predicates (MobileBottomNav) does not pull a
 * component tree in behind them.
 *
 * Adding a fourth mode is one entry here plus one icon in FocusModeTabs.
 */

/** Ordered left-to-right exactly as the tab bar paints them. */
export const FOCUS_MODES = [
    { id: 'freestyle', label: 'Freestyle', path: '/focus' },
    { id: 'circuit', label: 'Circuit', path: '/circuits' },
    { id: 'rituals', label: 'Rituals', path: '/rituals', module: 'rituals' },
];

/** The mode an unrecognised path resolves to. */
export const DEFAULT_FOCUS_MODE = 'freestyle';

const BY_PATH = new Map(FOCUS_MODES.map(m => [m.path, m.id]));

/** Every path the Focus page answers on. */
export const FOCUS_PATHS = FOCUS_MODES.map(m => m.path);

/**
 * The pathname React Router HANDS US is not the pathname it MATCHED ON.
 *
 * `<Route path="/circuits">` matches `/circuits/` and `/Circuits` — trailing
 * slashes are optional and matching is case-insensitive by default — but router
 * never rewrites `location.pathname`, so `useLocation()` returns the URL the user
 * actually typed. An exact Map lookup therefore missed on both, and missing here
 * is not a cosmetic slip: the dispatcher fell back to Freestyle on the Circuit
 * route WHILE Header, reading the same registry, decided the page was not a Focus
 * page and rendered no tab bar. Wrong view, no way out.
 *
 * Normalising here rather than at each call site is the point of the registry:
 * every consumer inherits the same answer.
 *
 * `'/'` survives as `'/'` — stripping its slash would leave `''`, and an empty
 * key is one accidental `FOCUS_MODES` entry away from matching the home route.
 * A non-string (a caller reading an undefined location during render) yields
 * `''`, which matches nothing and cannot throw.
 */
function normalizePath(pathname) {
    if (typeof pathname !== 'string') return '';
    const trimmed = pathname.toLowerCase().replace(/\/+$/, '');
    return trimmed || '/';
}

/**
 * Mode id for a pathname. Unknown paths resolve to the default rather than
 * throwing — this is read during render and must never be able to break a page.
 */
export function modeFromPath(pathname) {
    return BY_PATH.get(normalizePath(pathname)) || DEFAULT_FOCUS_MODE;
}

/** True when `pathname` is one of the Focus page's own routes. */
export function isFocusPath(pathname) {
    return BY_PATH.has(normalizePath(pathname));
}

/**
 * The mode the Focus page ACTUALLY shows for a pathname — the route, with the
 * single override the dispatcher applies: a live circuit outranks the URL, so
 * landing on /focus mid-run shows the runner rather than a second session's
 * config.
 *
 * Lives here because pages/Focus.jsx is not the only component that needs the
 * answer. CircuitRunBanner has to know whether the route the user is on is
 * already showing the runner, or it stacks "a Circuit is running, tap to go to
 * it" on top of the running circuit. Two copies of an override rule is how the
 * old four-predicates-for-one-question bug started; there is one copy.
 *
 * `circuitRunning` is RUNNING ONLY, never `paused` or `paused-editing` — see the
 * dispatcher's own note for why editing must not override the route.
 */
export function resolveFocusMode(pathname, { circuitRunning = false } = {}) {
    return circuitRunning ? 'circuit' : modeFromPath(pathname);
}
